import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, CalendarDays, MapPin, Sparkles, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const GALLERY = [
  {
    src: '/events/mgi-2.png',
    caption: 'IIY Phytoventra Director Rupinderjit presents the award to the 2nd runner-up on stage.',
    featured: true,
  },
  { src: '/events/mgi-1.png', caption: 'The coronation moment during the grand finale.' },
  { src: '/events/mgi-3.png', caption: 'The winner and runners-up with their certificates.' },
  { src: '/events/mgi-4.png', caption: 'Miss Goa International 2026 — the newly crowned winner.' },
  { src: '/events/mgi-5.png', caption: 'The crowned winner on the finale runway.' },
];

export default function MissGoaEvent() {
  const eventLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Miss Goa International UK 2026 — Grand Finale & Coronation',
    startDate: '2026-09-06T16:00:00+01:00',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: 'Radisson RED London Heathrow',
      address: { '@type': 'PostalAddress', addressLocality: 'London', postalCode: 'UB7 0DU', addressCountry: 'GB' },
    },
    image: ['https://iiypv.com/events/mgi-4.png'],
    description:
      'The first-ever Miss Goa International UK Grand Finale & Coronation Ceremony, celebrating Goan heritage, culture and talent. IIY Phytoventra Ltd was the Official Wellbeing Partner.',
    sponsor: { '@type': 'Organization', name: 'IIY Phytoventra Ltd', url: 'https://iiypv.com/' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-white">
      <SEO
        title="Miss Goa International UK 2026 | Wellbeing Partner | IIY"
        description="IIY Phytoventra Ltd was the Official Wellbeing Partner of the first Miss Goa International UK 2026 Grand Finale in London. See highlights from our first sponsorship event."
        pathOverride="/miss-goa-international"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(eventLd) }} />

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 border border-amber-300/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800" data-testid="event-eyebrow">
            <Crown className="w-3.5 h-3.5" /> Official Wellbeing Partner
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-emerald-900 leading-tight" data-testid="event-h1">
            Miss Goa International UK 2026
          </h1>
          <p className="mt-3 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            Our very first sponsorship event — IIY Phytoventra Ltd was proud to be the
            Official Wellbeing Partner of the inaugural Miss Goa International UK Grand Finale.
          </p>
          <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-emerald-700" /> Saturday, 6 September 2026</li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-700" /> Radisson RED London Heathrow (UB7 0DU)</li>
          </ul>
        </motion.div>

        <hr className="my-10 border-emerald-700/10" />

        {/* About the event */}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div className="prose prose-slate max-w-none text-[15px] md:text-base leading-relaxed text-slate-700 space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-emerald-900">About the event</h2>
            <p>
              The <strong>Miss Goa International UK 2026</strong> was the first-ever edition of the pageant in the
              United Kingdom — a glamorous Grand Finale and Coronation Ceremony that brought together the vibrant
              spirit of Goan heritage, culture and talent on an international stage in London, hosting distinguished
              guests, diplomats and community leaders.
            </p>
            <p>
              As the <strong>Official Wellbeing Partner</strong>, IIY Phytoventra was delighted to support a platform
              that celebrates grace, ambition and cultural unity within the global diaspora — values that sit close to
              our own mission of empowering healthier, more active lifestyles.
            </p>
            <p>
              A proud highlight of the evening: our Director, <strong>Rupinderjit</strong>, presented the award to the
              <strong> 2nd runner-up</strong> during the coronation ceremony.
            </p>
          </div>

          <div className="rounded-3xl bg-emerald-900 text-white p-7 md:p-8 shadow-xl">
            <Sparkles className="w-6 h-6 text-amber-300" />
            <h3 className="mt-3 text-xl font-bold font-heading">A milestone moment</h3>
            <p className="mt-2 text-emerald-50/90 text-sm leading-relaxed">
              This marked IIY Phytoventra's first brand sponsorship event — a step in sharing our
              wellbeing mission with a wider community.
            </p>
            <Link
              to="/products"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 text-emerald-950 font-bold py-2.5 px-5 text-sm hover:bg-amber-300 transition-colors"
              data-testid="event-shop-cta"
            >
              Explore our products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Gallery */}
        <h2 className="mt-16 mb-6 text-2xl md:text-3xl font-bold font-heading text-emerald-900" data-testid="event-gallery-heading">
          Event highlights
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="event-gallery">
          {GALLERY.map((g, i) => (
            <motion.figure
              key={g.src}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`overflow-hidden rounded-2xl border border-emerald-700/15 bg-white shadow-sm ${g.featured ? 'sm:col-span-2 lg:col-span-2 lg:row-span-2' : ''}`}
              data-testid={`event-photo-${i}`}
            >
              <div className={`w-full ${g.featured ? 'aspect-[16/10]' : 'aspect-[4/3]'} bg-emerald-50`}>
                <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <figcaption className="p-4 text-[13px] text-slate-600 leading-snug">{g.caption}</figcaption>
            </motion.figure>
          ))}
        </div>

        <p className="mt-8 text-[12px] text-muted-foreground">
          IIY Phytoventra Ltd &mdash; Official Wellbeing Partner, Miss Goa International UK 2026.
        </p>
      </div>
    </div>
  );
}
